import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { API_BASE_URL } from '../config/api'
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress
} from '@mui/material'
import {
  Person,
  LocationOn,
  Phone,
  Email,
  Agriculture,
  Pets,
  Assignment,
  Message,
  ArrowBack,
  CheckCircle,
  Schedule,
  Warning,
  Send
} from '@mui/icons-material'

interface FarmerData {
  id: string
  name: string
  email: string
  phone: string
  location: string
  bio: string
  profile_image_url?: string
  created_at: string
  total_productions: number
  active_productions: number
  completed_productions: number
  success_rate: number
}

interface Production {
  id: string
  production_name: string
  production_type: 'crop' | 'animal'
  variety_name: string
  start_date: string
  expected_harvest: string
  current_stage: string
  progress_percentage: number
  status: 'active' | 'completed' | 'delayed' | 'at_risk'
}

const FarmerProfile: React.FC = () => {
  const { farmerId } = useParams<{ farmerId: string }>()
  const navigate = useNavigate()
  const [farmer, setFarmer] = useState<FarmerData | null>(null)
  const [productions, setProductions] = useState<Production[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Chat dialog state
  const [chatOpen, setChatOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [chatError, setChatError] = useState<string | null>(null)
  const [chatSuccess, setChatSuccess] = useState(false)

  const fetchFarmerData = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      
      // Fetch farmer profile
      const farmerResponse = await fetch(`${API_BASE_URL}/farmers/${farmerId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!farmerResponse.ok) {
        throw new Error(`Failed to fetch farmer profile: ${farmerResponse.status}`)
      }

      const farmerData = await farmerResponse.json()
      
      // Transform farmer data from backend response structure
      const farmer = farmerData.data?.farmer
      const transformedFarmer: FarmerData = {
        id: farmer?.id || farmerId || '',
        name: `${farmer?.first_name || ''} ${farmer?.last_name || ''}`.trim() || 'Unknown Farmer',
        email: farmer?.email || 'Not provided',
        phone: farmer?.phone || 'Not provided',
        location: farmer?.location || 'Not provided',
        bio: farmer?.bio || 'No bio available',
        profile_image_url: farmer?.profile_image_url,
        created_at: farmer?.created_at || new Date().toISOString(),
        total_productions: 0, // Will be calculated from productions
        active_productions: 0, // Will be calculated from productions
        completed_productions: 0, // Will be calculated from productions
        success_rate: 0 // Will be calculated from productions
      }

      setFarmer(transformedFarmer)

      // Fetch farmer productions
      const productionsResponse = await fetch(`${API_BASE_URL}/farmer-productions?farmer_id=${farmerId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (productionsResponse.ok) {
        const productionsData = await productionsResponse.json()
        const transformedProductions: Production[] = productionsData.productions?.map((prod: any) => ({
          id: prod.id || prod.production_id,
          production_name: prod.production_name || prod.name,
          production_type: prod.production_type || (prod.template?.category === 'crop' ? 'crop' : 'animal'),
          variety_name: prod.variety_name || prod.template?.variety_name || 'Unknown Variety',
          start_date: prod.start_date || prod.created_at,
          expected_harvest: prod.expected_harvest_date || prod.expected_end_date,
          current_stage: prod.current_stage?.name || prod.current_stage || 'Not Started',
          progress_percentage: prod.progress_percentage || 0,
          status: prod.status === 'active' ? 'active' : 
                  prod.status === 'completed' ? 'completed' :
                  prod.progress_percentage < 50 ? 'at_risk' : 'active'
        })) || []
        
        setProductions(transformedProductions)
        
        // Update farmer statistics based on productions
        const totalProductions = transformedProductions.length
        const activeProductions = transformedProductions.filter(p => p.status === 'active').length
        const completedProductions = transformedProductions.filter(p => p.status === 'completed').length
        const successRate = totalProductions > 0 ? Math.round((completedProductions / totalProductions) * 100) : 0
        
        setFarmer(prev => prev ? {
          ...prev,
          total_productions: totalProductions,
          active_productions: activeProductions,
          completed_productions: completedProductions,
          success_rate: successRate
        } : null)
      }
      
    } catch (error) {
      console.error('Error fetching farmer data:', error)
      setError(error instanceof Error ? error.message : 'Failed to load farmer profile')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (farmerId) {
      fetchFarmerData()
    }
  }, [farmerId])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success'
      case 'completed': return 'primary'
      case 'delayed': return 'warning'
      case 'at_risk': return 'error'
      default: return 'default'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle />
      case 'completed': return <CheckCircle />
      case 'delayed': return <Schedule />
      case 'at_risk': return <Warning />
      default: return <Schedule />
    }
  }

  const handleSendMessage = async () => {
    if (!message.trim() || !farmerId) return

    try {
      setSending(true)
      setChatError(null)
      const token = localStorage.getItem('token')

      // First, get or create conversation
      const convResponse = await fetch(`${API_BASE_URL}/admin-messaging/conversation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          farmerId: farmerId,
          subject: 'General Support'
        })
      })

      if (!convResponse.ok) {
        throw new Error('Failed to create conversation')
      }

      const convData = await convResponse.json()
      const conversationId = convData.data?.conversation?.id

      if (!conversationId) {
        throw new Error('No conversation ID received')
      }

      // Send the message
      const msgResponse = await fetch(`${API_BASE_URL}/admin-messaging/message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          conversationId,
          message: message.trim(),
          messageType: 'text'
        })
      })

      if (!msgResponse.ok) {
        throw new Error('Failed to send message')
      }

      // Success
      setChatSuccess(true)
      setMessage('')
      setTimeout(() => {
        setChatSuccess(false)
        setChatOpen(false)
      }, 2000)

    } catch (error) {
      console.error('Error sending message:', error)
      setChatError(error instanceof Error ? error.message : 'Failed to send message')
    } finally {
      setSending(false)
    }
  }

  const handleOpenChat = () => {
    setChatOpen(true)
    setChatError(null)
    setChatSuccess(false)
  }

  const handleCloseChat = () => {
    if (!sending) {
      setChatOpen(false)
      setMessage('')
      setChatError(null)
      setChatSuccess(false)
    }
  }

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Farmer Profile</Typography>
        <LinearProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/productions')}
          sx={{ mb: 2 }}
        >
          Back to Productions
        </Button>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    )
  }

  if (!farmer) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/productions')}
          sx={{ mb: 2 }}
        >
          Back to Productions
        </Button>
        <Alert severity="warning">
          Farmer not found
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/productions')}
            sx={{ mr: 2 }}
          >
            Back to Productions
          </Button>
          <Typography variant="h4">
            Farmer Profile
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Message />}
          onClick={handleOpenChat}
        >
          Send Message
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Profile Information */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                <Avatar
                  src={farmer.profile_image_url}
                  sx={{ width: 120, height: 120, mb: 2, bgcolor: 'primary.main' }}
                >
                  <Person sx={{ fontSize: 60 }} />
                </Avatar>
                <Typography variant="h5" fontWeight="bold" textAlign="center">
                  {farmer.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Member since {new Date(farmer.created_at).toLocaleDateString()}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Email />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Email" 
                    secondary={farmer.email}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Phone />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Phone" 
                    secondary={farmer.phone}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <LocationOn />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Location" 
                    secondary={farmer.location}
                  />
                </ListItem>
              </List>

              {farmer.bio && farmer.bio !== 'No bio available' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Bio
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {farmer.bio}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Statistics and Productions */}
        <Grid item xs={12} md={8}>
          {/* Statistics Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">
                    {farmer.total_productions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Productions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">
                    {farmer.active_productions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="info.main">
                    {farmer.completed_productions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="warning.main">
                    {farmer.success_rate}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Success Rate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Productions List */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Production History
              </Typography>
              
              {productions.length === 0 ? (
                <Alert severity="info">
                  No productions found for this farmer
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Production</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Progress</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productions.map((production) => (
                        <TableRow key={production.id}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {production.production_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {production.variety_name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              icon={production.production_type === 'crop' ? <Agriculture /> : <Pets />}
                              label={production.production_type}
                              size="small"
                              color={production.production_type === 'crop' ? 'success' : 'info'}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ minWidth: 100 }}>
                              <LinearProgress
                                variant="determinate"
                                value={production.progress_percentage}
                                sx={{ mb: 0.5 }}
                              />
                              <Typography variant="caption">
                                {production.progress_percentage}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(production.status)}
                              label={production.status === 'at_risk' ? 'At Risk' : 
                                     production.status.charAt(0).toUpperCase() + production.status.slice(1)}
                              color={getStatusColor(production.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              startIcon={<Assignment />}
                              onClick={() => navigate(`/production-plans/${production.id}`)}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Chat Dialog */}
      <Dialog open={chatOpen} onClose={handleCloseChat} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Message />
            <Typography variant="h6">Send Message to {farmer?.name}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {chatError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {chatError}
            </Alert>
          )}
          {chatSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Message sent successfully! The farmer will receive it in their messages.
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Your Message"
            placeholder="Type your message to the farmer..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={sending || chatSuccess}
            sx={{ mt: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This message will be sent to the farmer's support chat and they will be notified.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseChat} disabled={sending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={20} /> : <Send />}
            onClick={handleSendMessage}
            disabled={!message.trim() || sending || chatSuccess}
          >
            {sending ? 'Sending...' : 'Send Message'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default FarmerProfile
